import { Loader, SideMenu } from '@/components';
import Modal from '@/components/modal';
import { TopMenu } from '@/components/top-menu';
import { CasinoFooter } from '@/components/CasinoFooter';
import { B64ToHex, isValidB64SuiAddress, isValidSuinsName } from '@/utils';
import { createRootRoute, Outlet, redirect, useRouterState, useSearch } from '@tanstack/react-router';
import { useEffect, useState, lazy, Suspense } from 'react';
import { useCookies } from 'react-cookie';
import { useChatStore } from '@/stores/chat-store';
import { MobileBottomNav } from '@/components/MobileBottomNav';
import { useRandomnessModalStore } from '@/stores/randomness-modal-store';
import { getAllUrlParams } from '@/utils/url-params';
import { useSuinsResolver } from '@/hooks';
import { useScrollRestoration } from '@/hooks/useScrollRestoration';
import { readSidebarCollapsedFromStorage, STANDALONE_ROUTES } from '@/consts';

const ChatLazy = lazy(() => import('@/components/chat/Chat').then((m) => ({ default: m.Chat })));
const PlayResponsiblyContentLazy = lazy(() =>
	import('@/components/legal/play-responsibly-content').then((m) => ({ default: m.PlayResponsiblyContent })),
);
const TermsOfServiceContentLazy = lazy(() =>
	import('@/components/legal/terms-of-service-content').then((m) => ({ default: m.TermsOfServiceContent })),
);
const PrivacyPolicyContentLazy = lazy(() => import('@/components/legal/privacy-policy-content').then((m) => ({ default: m.PrivacyPolicyContent })));
const RandomnessGuarantyContentLazy = lazy(() =>
	import('@/components/legal/randomness-guaranty-content').then((m) => ({ default: m.RandomnessGuarantyContent })),
);

export function RouteComponent() {
	const isLoading = false;
	const { location } = useRouterState();
	const [, setCookie] = useCookies(['referrer']);
	const { resolveAddress } = useSuinsResolver();
	const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(readSidebarCollapsedFromStorage);
	const isStandaloneRoute = STANDALONE_ROUTES.some((route) => location.pathname === route || location.pathname.startsWith(`${route}/`));
	const isChatOpen = useChatStore((s) => s.isOpen);
	const isRandomnessModalOpen = useRandomnessModalStore((state) => state.isOpen);
	const closeRandomnessModal = useRandomnessModalStore((state) => state.closeRandomnessModal);
	const openRandomnessModal = useRandomnessModalStore((state) => state.openRandomnessModal);

	const referralParam = useSearch({
		strict: false,
	}) as Record<string, string>;
	useEffect(() => {
		const resolveAndSetReferrer = async () => {
			const playParam = Object.keys(referralParam).find((key) => key.toLowerCase() === 'play');

			let referrerAddress = null;

			if (playParam) {
				const playValue = referralParam[playParam];

				if (isValidSuinsName(playValue)) {
					const resolvedAddress = await resolveAddress(playValue);
					if (resolvedAddress) {
						referrerAddress = resolvedAddress;
					}
				}

				if (isValidB64SuiAddress(playValue)) {
					referrerAddress = B64ToHex(playValue);
				}

				if (referrerAddress) {
					try {
						setCookie('referrer', referrerAddress, {
							maxAge: 7 * 24 * 60 * 60, // 7 days in seconds
							path: '/', // Available across the entire site
						});
					} catch (error) {
						console.error('Failed to set referrer cookie:', error);
					}
				} else {
					// console.log('No referrer address found');
				}
			}
		};

		resolveAndSetReferrer();
	}, [referralParam, resolveAddress, setCookie]);

	// State for modals
	const [isPlayResponsiblyModalOpen, setPlayResponsiblyModalOpen] = useState(false);
	const [isTermsModalOpen, setTermsModalOpen] = useState(false);
	const [isPrivacyModalOpen, setPrivacyModalOpen] = useState(false);

	useScrollRestoration(location);

	if (isStandaloneRoute) {
		return <Outlet />;
	}

	if (isLoading)
		return (
			<div className="h-dvh w-dvw">
				<Loader />
			</div>
		);

	return (
		<>
			<TopMenu />
			<SideMenu onCollapseChange={setIsSidebarCollapsed} />
			<div key="fade" className="pointer-events-none fixed z-20 w-full bg-linear-to-b from-background to-transparent h-20"></div>
			<div
				key="main"
				className={`h-full transition-all duration-300 max-md:pb-[calc(env(safe-area-inset-bottom)+3em)] ${isSidebarCollapsed ? 'md:ml-18' : 'md:ml-52'} ${isChatOpen ? 'md:mr-80' : 'md:mr-0'}`}
			>
				<div className="relative flex min-h-svh w-full flex-col">
					<main className="flex-1 flex flex-col min-h-screen">
						<Outlet />
					</main>
					<MobileBottomNav />
					<CasinoFooter
						onOpenPlayResponsibly={() => setPlayResponsiblyModalOpen(true)}
						onOpenProvablyFair={openRandomnessModal}
						onOpenTerms={() => setTermsModalOpen(true)}
						onOpenPrivacy={() => setPrivacyModalOpen(true)}
					/>
				</div>
			</div>
			{isChatOpen ? (
				<Suspense fallback={null}>
					<ChatLazy key="chat-component" />
				</Suspense>
			) : null}
			<Modal key="play-responsibly-modal" isOpen={isPlayResponsiblyModalOpen} onClose={() => setPlayResponsiblyModalOpen(false)}>
				<div className="mr-1 max-h-[80vh] overflow-y-auto p-6">
					<Suspense fallback={<div className="h-24 w-full" />}>
						<PlayResponsiblyContentLazy />
					</Suspense>
				</div>
			</Modal>

			<Modal key="randomness-guarantee-modal" isOpen={isRandomnessModalOpen} onClose={closeRandomnessModal}>
				<div className="mr-1 max-h-[80vh] overflow-y-auto p-6">
					<Suspense fallback={<div className="h-24 w-full" />}>
						<RandomnessGuarantyContentLazy />
					</Suspense>
				</div>
			</Modal>

			<Modal key="terms-modal" isOpen={isTermsModalOpen} onClose={() => setTermsModalOpen(false)}>
				<div className="mr-1 max-h-[80vh] overflow-y-auto p-6">
					<Suspense fallback={<div className="h-24 w-full" />}>
						<TermsOfServiceContentLazy />
					</Suspense>
				</div>
			</Modal>

			<Modal key="privacy-modal" isOpen={isPrivacyModalOpen} onClose={() => setPrivacyModalOpen(false)}>
				<div className="mr-1 max-h-[80vh] overflow-y-auto p-6">
					<Suspense fallback={<div className="h-24 w-full" />}>
						<PrivacyPolicyContentLazy />
					</Suspense>
				</div>
			</Modal>
		</>
	);
}

export const Route = createRootRoute({
	component: RouteComponent,

	beforeLoad: ({ location }) => {
		if (location.pathname.startsWith('/oauth-callback')) {
			let state = getAllUrlParams().get('state');

			if (state) {
				try {
					state = decodeURIComponent(state);
				} catch (err) {
					console.error('Failed to decode state:', state, err);
				}

				throw redirect({
					to: state,
					replace: true,
				});
			}

			throw redirect({
				to: '/',
				replace: true,
			});
		}
	},
});
